import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import {
  listenToShortstayReservaionData,
} from "../../../../redux/shortstayReservationSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";

// ✅ MUI icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import PaymentIcon from "@mui/icons-material/Payment";
import HotelIcon from "@mui/icons-material/Hotel";

const ShortBookingDetails: React.FC = () => {
  const { userId, reservationId } = useParams<{ userId: string; reservationId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const reservation = useSelector(
    (state: RootState) => state.shortstayReservations.reservationById
  );

  const listing = useSelector((state: RootState) =>
    state.shortstays.listings.find(
      (l) => l.listingId === reservation?.listingId
    )
  );

  useEffect(() => {
    if (reservationId) dispatch(listenToShortstayReservaionData(reservationId));
  }, [dispatch, reservationId]);

  useEffect(() => {
    if (reservation?.listingId && !listing) {
      dispatch(listenToShortstayData(reservation.listingId));
    }
  }, [dispatch, reservation, listing]);

  const photo = listing?.photos?.[0] || "/default-shortstay.jpg";
  const fullAddress =
    listing?.address?.street ||
    listing?.city ||
    listing?.address?.city ||
    "Unknown location";
  const latitude = listing?.address?.latitude || "-1.2921";
  const longitude = listing?.address?.longitude || "36.8219";

  // ✅ Reuse status flag renderer
  const renderFlags = (res: any) => (
    <div style={styles.flags}>
      <span style={res.isConfirmed ? styles.flagActive : styles.flagInactive}>
        {res.isConfirmed ? <CheckCircleIcon fontSize="small" /> : <HourglassBottomIcon fontSize="small" />}
        {res.isConfirmed ? " Confirmed" : " Pending"}
      </span>
      {res.isCancelled && (
        <span style={styles.flagCancelled}>
          <CancelIcon fontSize="small" /> Cancelled
        </span>
      )}
      {res.isRefunded && (
        <span style={styles.flagRefunded}>
          <ReplayIcon fontSize="small" /> Refunded
        </span>
      )}
      {res.isPaid && (
        <span style={styles.flagActive}>
          <PaymentIcon fontSize="small" /> Paid
        </span>
      )}
      {res.hasCheckedIn && (
        <span style={styles.flagActive}>
          <HotelIcon fontSize="small" /> Checked In
        </span>
      )}
    </div>
  );

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Booking Details</h1>

      {reservation && listing ? (
        <div style={styles.card}>
          <img src={photo} alt="Stay" style={styles.image} />
          <div style={styles.info}>
            <p><strong>Name:</strong> {listing.title}</p>
            <p style={styles.meta}>Check-in: {reservation.checkIn}</p>
            <p style={styles.meta}>Check-out: {reservation.checkOut}</p>
            <p style={styles.meta}>📍 Address: {fullAddress}</p>

            {/* ✅ Status flags here */}
            {renderFlags(reservation)}

            <button
              style={styles.actionBtn}
              onClick={() =>
                window.open(
                  `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
                  "_blank"
                )
              }
            >
              🧭 Get Directions →
            </button>

            <button
              style={styles.actionBtn}
              onClick={() => navigate(`/shortchecking/${reservation.listingId}`)}
            >
              📜 Entry Guide →
            </button>

            <button
              style={styles.actionBtn}
              onClick={() =>
                reservation.guestId && reservation.listingId &&
                navigate(`/GuestShortCancelBooking/${reservation.guestId}/${reservation.listingId}`)
              }
            >
              Cancel Booking →
            </button>

            <button
              style={styles.actionBtn}
              onClick={() =>
                reservation.guestId && reservation.id &&
                navigate(`/shortreceipt/${reservation.guestId}/${reservation.id}`)
              }
            >
              Get Receipt →
            </button>
          </div>
        </div>
      ) : (
        <p style={styles.noData}>No reservation found.</p>
      )}

      <footer style={styles.footer}>
        <button
          onClick={() => navigate(`/shortbookings/${userId}`)}
          style={styles.backButton}
        >
          ← Back to My Bookings
        </button>
      </footer>
    </div>
  );
};

export default ShortBookingDetails;

// --- STYLES ---
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "80px 20px 120px",
    color: "#fff",
    fontFamily: "Poppins, sans-serif",
  },
  title: {
    fontSize: "2rem",
    textAlign: "center",
    fontWeight: 600,
    marginBottom: "30px",
  },
  card: {
    backgroundColor: "#111",
    border: "1px solid #bf9b30",
    borderRadius: "14px",
    overflow: "hidden",
    maxWidth: "600px",
    margin: "0 auto",
    boxShadow: "0 10px 30px rgba(191, 155, 48, 0.3)",
  },
  image: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
  },
  info: {
    padding: "18px",
  },
  meta: {
    fontSize: "0.95rem",
    color: "#ccc",
    marginBottom: "8px",
  },

  // ✅ Flags
  flags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    margin: "10px 0",
  },
  flagActive: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    background: "linear-gradient(90deg, #00c853, #64dd17)",
    color: "#000",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  flagInactive: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    background: "#555",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  flagCancelled: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    background: "#ff4444",
    color: "#fff",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  flagRefunded: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    background: "linear-gradient(90deg, #42a5f5, #1e88e5)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  actionBtn: {
    marginTop: "12px",
    padding: "12px 20px",
    border: "none",
    borderRadius: "24px",
    background: "linear-gradient(to right, #fff, #bf9b30)",
    color: "#000",
    fontWeight: 600,
    cursor: "pointer",
    display: "block",
    width: "100%",
  },
  noData: {
    textAlign: "center",
    color: "#888",
    fontSize: "1.2rem",
  },
  footer: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#000",
    padding: "16px 20px",
    borderTop: "1px solid #333",
    textAlign: "center",
  },
  backButton: {
    padding: "12px 30px",
    fontSize: "16px",
    fontWeight: 600,
    borderRadius: "30px",
    border: "none",
    background: "linear-gradient(to right, #fff, #bf9b30)",
    color: "#000",
    cursor: "pointer",
    maxWidth: "280px",
    width: "100%",
    margin: "0 auto",
  },
};
